import React, { useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch } from "../../store";
import { forgotPassword } from "../../store/actions/auth.action";
import ToastMessage from "../../utils/toastMessage";
import { App, Button, message } from "antd";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import logoImage from "../../assets/images/logo.png";
import theme from "../../themes";

const ForgotPassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  
  // Yup validation schema
  const schema = yup
    .object({
      email: yup
        .string()
        .email("Please enter a valid email address")
        .required("Email is required")
        .trim(),
    })
    .required();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const onSubmit = async (data: { email: string }) => {
    setLoading(true);
    try {
      await dispatch(forgotPassword(data)).unwrap();
      reset();
      setLoading(false);
      toast.success("A reset link has been sent to your email.", 5, <CheckCircleIcon />);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  return (
    <Container>
      <LogoContainer>
        <LogoWrapper>
          <LogoImage src={logoImage} alt="Lytte.ai Logo" />
          <Title>Lytte.ai</Title>
        </LogoWrapper>
      </LogoContainer>

      <FormCard>
        <FormTitle>Forget your password? Enter your email below to reset.</FormTitle>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <FieldRow>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email address"
                disabled={loading}
                className={errors.email ? "error" : ""}
                autoFocus
                {...register("email")}
              />
            </FieldRow>
          </FormGroup>

          <Divider />

          <ButtonLinkRow>
            <BackLink to="/">Back to login</BackLink>
            <SubmitButton htmlType="submit" disabled={loading || !isValid}>
              {loading ? "Sending..." : "Send reset link"}
            </SubmitButton>
          </ButtonLinkRow>
        </Form>
      </FormCard>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  padding: 20px;
  padding-top: 20vh;
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 62px;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  font-style: italic;
`;

const FormCard = styled.div`
  background: white;
  padding: 48px 32px 24px 32px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 560px;
`;

const FormTitle = styled.p`
  text-align: left;
  margin: 0 0 32px 0;
  font-size: 18px;
  font-weight: 500;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 16px;
  text-align: left;
  width: 140px;
`;

const Input = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  background: white;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00b4d8;
  }

  &:hover {
    border-color: #bdbdbd;
  }

  &::placeholder {
    color: #9e9e9e;
  }

  &.error {
    border-color: #e74c3c;
  }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: #e5e7eb;
  margin: 32px -16px 24px -16px;
`;

const ButtonLinkRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackLink = styled(Link)`
  color: #9e9e9e;
  font-size: 16px;
  font-weight: 400;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled(Button)`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #047857 !important;
    color: #fff !important;
  }

  &:disabled {
    background: ${theme.colors.grey2};
    cursor: not-allowed;
  }
`;

export default ForgotPassword;
